import { CSSInterpolation } from '@emotion/serialize';
import { mq } from '@hangar31/rc-layout';
/* OG Colors:
  solidMedYellow: 'rgba(246, 236, 155, 1)',
  bgMain: 'rgba(243, 244, 249, 1)',
  textBrightYellow: 'rgba(239, 254, 71, 1)',
  ggInput: 'rgba(249, 252, 253, 1)',
  black: 'rgba(0, 0, 0, 1)',
  bgUnClickable: 'rgba(249, 251, 253, 1)',
  white: 'rgba(255, 255, 255, 1)',
  solidMedPurple: 'rgba(116, 39, 173, 1)',
  bgStroke: 'rgba(226, 228, 239, 1)',
  bgLightGrey: 'rgba(241, 244, 248, 1)',
  textLightPurple: 'rgba(170, 155, 211, 1)',
  solidDarkPurple: 'rgba(71, 50, 133, 1)'
  textLightGrey: 'rgba(201, 202, 214, 1)',
  textBrightPurple: 'rgba(173, 52, 161, 1)',
  textExtraDarkGrey: 'rgba(27, 38, 48, 1)',
  solidLightPurple: 'rgba(174, 140, 195, 1)',
  textDarkGrey: 'rgba(88, 93, 100, 1)',
  textMedGrey: 'rgba(147, 148, 158, 1)',
*/

const colors = {
  background: 'rgba(243, 244, 249, 1)',
  // Legacy alias kept for admin team + ministry [ministryId] pages that still
  // reference theme.colors.bgLightGrey. Value matches the original OG
  // theme's bgLightGrey. Remove this alias once those two files are
  // migrated to `background`.
  bgLightGrey: 'rgba(241, 244, 248, 1)',
  black: 'rgba(0, 0, 0, 1)',
  blackLight: 'rgba(27, 38, 48, 1)',
  border: 'rgba(226, 228, 239, 1)',
  clickable: 'rgba(123, 44, 255, 1)',
  clickableLight: 'rgba(170, 155, 211, 1)',
  disabled: 'rgba(226, 228, 239, 1)',
  error: 'rgba(181, 70, 15, 1)',
  errorLight: 'rgba(251, 238, 227, 1)',
  grayDark: 'rgba(88, 93, 100, 1)',
  grayLight: 'rgba(201, 202, 214, 1)',

  grayMed: 'rgba(147, 148, 158, 1)',

  // The old magenta accent was dropped from the brand (one purple + lime);
  // token kept for its 8 call sites, now pointing at the brand amethyst.
  highlight: 'rgba(123, 44, 255, 1)',

  ink: 'rgba(15, 11, 26, 1)',

  // Semantic state colors — added for the internal redesign. Use these for
  // status pills, alerts, and any "good/bad/warning" signal in the UI.
  primary: 'rgba(123, 44, 255, 1)',

  // Pale amethyst wash. Derived in OKLCH at the base's perceptual hue (291°)
  // — sRGB white-mixes of violet drift ~10° pink (oklch 0.955 0.042 291).
  primaryLight: 'rgba(240, 236, 255, 1)',
  secondary: 'rgba(239, 254, 71, 1)',
  success: 'rgba(31, 138, 76, 1)',
  successLight: 'rgba(232, 246, 238, 1)',
  warning: 'rgba(138, 111, 0, 1)',
  warningLight: 'rgba(255, 248, 217, 1)',
  white: 'rgba(255, 255, 255, 1)',
  whiteDark: 'rgba(249, 252, 253, 1)',
  // Hover wash, faint amethyst-family tint (oklch 0.968 0.018 291) — was an
  // old-purple-hue wash that read pink next to the new base.
  whiteHover: 'rgba(244, 243, 255, 1)',
};

const fontFamilies = { Roboto: "'Roboto', sans-serif" };

const radius = {
  md: '0.6rem',
};

const shadows = {
  lg: '0 1.6rem 3.2rem rgba(0, 0, 0, 0.15)',
  md: '0 0.8rem 1.6rem rgba(0, 0, 0, 0.1)',
  sm: '0rem 0.4rem 0.8rem rgba(0, 0, 0, 0.05)',
};

const fontSizes = {
  lg: ['2.4rem', undefined, '3rem'],
  md: ['1.8rem', undefined, '2.4rem'],
  sm: ['1.4rem', undefined, '1.8rem'],
  xl: ['1.8rem', undefined, '3rem', '3.6rem'],
  xs: ['1.2rem', undefined, '1.4rem'],
};
const fontWeights = {
  bl: '700',
  lt: '300',
  md: '500',
  rg: '400',
};

const font = (
  size: keyof typeof fontSizes = 'sm',
  weight: keyof typeof fontWeights = 'rg'
): CSSInterpolation => {
  return mq({
    fontFamily: fontFamilies.Roboto,
    fontSize: fontSizes[size],
    fontWeight: fontWeights[weight],
  });
};

const theme = {
  church: {
    bannerColor: {
      b: 0,
      g: 0,
      r: 0,
    },
    bannerStyle: 'WAVES',
    buttonColor: {
      b: 255,
      g: 44,
      r: 123,
    },
    buttonStyle: 'ROUND',
    buttonTextColor: {
      b: 255,
      g: 255,
      r: 255,
    },
  },
  colors,
  effects: {
    box: {
      background: colors.white,
      borderRadius: radius.md,
      boxShadow: shadows.md,
      padding: '0.1rem 1.5rem',
    },
  },
  font,
  fontFamilies,
  fontSizes,
  fontWeights,
  gradients: {
    button: `linear-gradient(150deg,rgb(47, 27, 104, 1) 0%, ${colors.clickable} 60%);`,
    main: `linear-gradient(90deg,rgb(47, 27, 104, 1) 10%, ${colors.clickable} 90%);`,
    whiteToBg: `linear-gradient(0deg, ${colors.background} 50%, ${colors.white} 80%)`,
  },
  H31Layout: {
    grid: {
      cols: 1,
      on: false,
    },
    positioning: {
      bottom: 0,
      top: 0,
    },
    spacing: {
      colGap: 3,
      padding: 2,
      rowGap: 3,
    },
  },
  radius,
  shadows,
};

export default theme;

export type OnArkTheme = typeof theme;
